import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { useNotification } from '../hooks/useNotification';
import axiosInstance from '../lib/axios';

/** Spec 05: `plugin` = skills + MCP servers + tools bundle installable from the CLI. */
export type MarketplaceItemType = 'skill' | 'tool' | 'mcp_server' | 'plugin';
export type MarketplaceReviewQueue = 'execution' | 'publication';

export interface MarketplaceReview {
  publicId: string;
  reviewType: string;
  reviewMode: string;
  verdict: string;
  severity: string;
  findings?: unknown[];
  notes?: string | null;
  createdAt?: string | null;
}

export interface MarketplaceMcpToolSnapshot {
  publicId: string;
  toolName: string;
  title?: string | null;
  description?: string | null;
  inputSchema?: Record<string, unknown>;
  outputSchema?: Record<string, unknown>;
  annotations?: Record<string, unknown>;
  contentHash?: string;
  createdAt?: string | null;
}

export interface MarketplaceVersion {
  publicId: string;
  itemPublicId?: string | null;
  versionNumber: number;
  semver?: string | null;
  manifest: Record<string, unknown>;
  payload: Record<string, unknown>;
  riskLevel: string;
  riskTags: unknown[];
  validationStatus: string;
  validationFindings: unknown[];
  executionStatus: string;
  publicationStatus: string;
  reviews?: MarketplaceReview[];
  mcpToolSnapshots?: MarketplaceMcpToolSnapshot[];
  createdAt?: string | null;
}

export interface MarketplaceItem {
  publicId: string;
  ownerScope: string;
  ownerUserId?: number | null;
  type: MarketplaceItemType;
  name: string;
  slug?: string;
  title: string;
  description?: string | null;
  visibility: string;
  executionStatus: string;
  publicationStatus: string;
  installCount: number;
  latestVersion?: MarketplaceVersion | null;
  activeExecutionVersion?: MarketplaceVersion | null;
  activePublicVersion?: MarketplaceVersion | null;
}

export interface MarketplaceInstallation {
  publicId: string;
  itemPublicId?: string | null;
  installedVersionPublicId?: string | null;
  autoUpdatePolicy: string;
  status: string;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface MarketplaceBinding {
  publicId: string;
  sourceType: string;
  itemPublicId?: string | null;
  installationPublicId?: string | null;
  targetType: MarketplaceTargetType;
  targetId: string;
  enabled: boolean;
  pinnedVersionPublicId?: string | null;
  runtimePolicyOverride?: string | null;
  config?: Record<string, unknown>;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface MarketplaceItemDetail extends MarketplaceItem {
  versions: MarketplaceVersion[];
  installations: MarketplaceInstallation[];
  bindings: MarketplaceBinding[];
}

export interface MarketplaceConfig {
  execution_review_mode: 'manual' | 'ai_assisted';
  publication_review_mode: 'manual' | 'ai_assisted';
  execution_auto_approve_low_risk: boolean;
  publication_auto_approve_low_risk: boolean;
  user_mcp_creation_enabled: boolean;
  ai_review_model?: string | null;
  ai_review_provider_code?: string | null;
}

export type MarketplaceTargetType = 'agent' | 'agent_template' | 'InternalAITask';

export interface MarketplaceTargetRegistry {
  agent_templates: Array<{ key: string; name: string }>;
  internal_ai_tasks: Array<{
    publicId: string;
    taskKey: string;
    name: string;
    description?: string | null;
  }>;
}

export interface MarketplaceAuditEvent {
  publicId: string;
  actorType: string;
  actorId?: number | null;
  userId?: number | null;
  action: string;
  resourceType: string;
  resourceId?: number | null;
  beforeData?: Record<string, unknown>;
  afterData?: Record<string, unknown>;
  success: boolean;
  error?: string | null;
  createdAt?: string | null;
}

export interface MarketplaceMcpHealth {
  itemPublicId: string;
  itemTitle?: string | null;
  itemName?: string | null;
  versionPublicId: string;
  versionNumber: number;
  semver?: string | null;
  serverLabel?: string | null;
  serverHost?: string | null;
  transport?: string | null;
  configScope?: string | null;
  executionStatus: string;
  publicationStatus: string;
  expectedTools: string[];
  allowedTools: string[];
  snapshotTools: string[];
  missingExpectedTools: string[];
  unexpectedSnapshotTools: string[];
  missingAllowedTools: string[];
  snapshotCount: number;
  driftStatus: 'ok' | 'warning' | 'drift' | 'unavailable';
  healthStatus: 'healthy' | 'warning' | 'unavailable';
  diagnostic: string;
  lastSnapshotAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface CreateMarketplaceItemInput {
  type: MarketplaceItemType;
  name: string;
  title: string;
  description?: string;
  visibility: 'private' | 'public';
}

export interface CreateMarketplaceVersionInput {
  semver?: string;
  payload: Record<string, unknown>;
}

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

export function useMarketplaceReviewQueue(queue?: MarketplaceReviewQueue) {
  return useQuery({
    queryKey: ['admin-marketplace-review-queue', queue ?? 'all'],
    queryFn: async () => {
      const response = await axiosInstance.get<ApiResponse<MarketplaceVersion[]>>(
        '/v1/admin/marketplace/review-queue',
        { params: queue ? { queue } : undefined }
      );
      return response.data;
    },
  });
}

export function useAdminMarketplaceItems(filters?: {
  type?: MarketplaceItemType;
  ownerScope?: 'user' | 'platform';
  visibility?: 'private' | 'public';
}) {
  return useQuery({
    queryKey: ['admin-marketplace-items', filters ?? {}],
    queryFn: async () => {
      const response = await axiosInstance.get<ApiResponse<MarketplaceItem[]>>(
        '/v1/admin/marketplace/items',
        {
          params: {
            type: filters?.type,
            owner_scope: filters?.ownerScope,
            visibility: filters?.visibility,
          },
        }
      );
      return response.data.data ?? [];
    },
  });
}

export function useAdminMarketplaceItem(itemPublicId?: string) {
  return useQuery({
    queryKey: ['admin-marketplace-item', itemPublicId],
    enabled: Boolean(itemPublicId),
    queryFn: async () => {
      const response = await axiosInstance.get<ApiResponse<MarketplaceItemDetail>>(
        `/v1/admin/marketplace/items/${itemPublicId}`
      );
      return response.data.data;
    },
  });
}

export function useMarketplaceConfig() {
  return useQuery({
    queryKey: ['admin-marketplace-config'],
    queryFn: async () => {
      const response = await axiosInstance.get<ApiResponse<MarketplaceConfig>>(
        '/v1/admin/marketplace/config'
      );
      return response.data.data;
    },
  });
}

export function useMarketplaceTargetRegistry() {
  return useQuery({
    queryKey: ['admin-marketplace-target-registry'],
    queryFn: async () => {
      const response = await axiosInstance.get<ApiResponse<MarketplaceTargetRegistry>>(
        '/v1/admin/marketplace/target-registry'
      );
      return response.data.data;
    },
  });
}

export function useMarketplaceAuditEvents() {
  return useQuery({
    queryKey: ['admin-marketplace-audit-events'],
    queryFn: async () => {
      const response = await axiosInstance.get<ApiResponse<MarketplaceAuditEvent[]>>(
        '/v1/admin/marketplace/audit-events',
        { params: { limit: 100 } }
      );
      return response.data.data ?? [];
    },
  });
}

export function useMarketplaceMcpHealth() {
  return useQuery({
    queryKey: ['admin-marketplace-mcp-health'],
    queryFn: async () => {
      const response = await axiosInstance.get<ApiResponse<MarketplaceMcpHealth[]>>(
        '/v1/admin/marketplace/mcp-health'
      );
      return response.data.data ?? [];
    },
  });
}

export function useUpdateMarketplaceConfig() {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (input: Partial<MarketplaceConfig>) => {
      const response = await axiosInstance.patch<ApiResponse<MarketplaceConfig>>(
        '/v1/admin/marketplace/config',
        input
      );
      return response.data;
    },
    onSuccess: () => {
      notification.success('Marketplace config updated');
      queryClient.invalidateQueries({ queryKey: ['admin-marketplace-config'] });
    },
    onError: (error: AxiosError<ApiResponse>) => {
      notification.error(error.response?.data?.message || 'Failed to update marketplace config');
    },
  });
}

export function useCreatePlatformMarketplaceItem() {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (input: CreateMarketplaceItemInput) => {
      const response = await axiosInstance.post<ApiResponse<MarketplaceItem>>(
        '/v1/admin/marketplace/platform-items',
        input
      );
      return response.data.data;
    },
    onSuccess: () => {
      notification.success('Platform marketplace item created');
      queryClient.invalidateQueries({ queryKey: ['admin-marketplace-items'] });
      queryClient.invalidateQueries({ queryKey: ['admin-marketplace-audit-events'] });
    },
    onError: (error: AxiosError<ApiResponse>) => {
      notification.error(error.response?.data?.message || 'Failed to create platform item');
    },
  });
}

export function useCreatePlatformMarketplaceVersion() {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: async ({
      itemPublicId,
      input,
    }: {
      itemPublicId: string;
      input: CreateMarketplaceVersionInput;
    }) => {
      const response = await axiosInstance.post<ApiResponse<MarketplaceVersion>>(
        `/v1/admin/marketplace/platform-items/${itemPublicId}/versions`,
        input
      );
      return response.data.data;
    },
    onSuccess: (_, variables) => {
      notification.success('Platform marketplace version created');
      queryClient.invalidateQueries({ queryKey: ['admin-marketplace-items'] });
      queryClient.invalidateQueries({
        queryKey: ['admin-marketplace-item', variables.itemPublicId],
      });
      queryClient.invalidateQueries({ queryKey: ['admin-marketplace-audit-events'] });
    },
    onError: (error: AxiosError<ApiResponse>) => {
      notification.error(error.response?.data?.message || 'Failed to create platform version');
    },
  });
}

export function useBindPlatformMarketplaceTargets() {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: async ({
      itemPublicId,
      targets,
    }: {
      itemPublicId: string;
      targets: Array<{
        target_type: MarketplaceTargetType;
        target_id: string;
        pinned_version_public_id?: string;
        runtime_policy_override?: 'allow' | 'ask' | 'deny';
        config?: Record<string, unknown>;
      }>;
    }) => {
      const response = await axiosInstance.post<ApiResponse<MarketplaceBinding[]>>(
        `/v1/admin/marketplace/platform-items/${itemPublicId}/bind-targets`,
        { targets }
      );
      return response.data.data ?? [];
    },
    onSuccess: (_, variables) => {
      notification.success('Platform marketplace item bound');
      queryClient.invalidateQueries({
        queryKey: ['admin-marketplace-item', variables.itemPublicId],
      });
      queryClient.invalidateQueries({ queryKey: ['admin-marketplace-audit-events'] });
    },
    onError: (error: AxiosError<ApiResponse>) => {
      notification.error(error.response?.data?.message || 'Failed to bind platform item');
    },
  });
}

export function useMarketplaceDecision(
  action: 'approve-execution' | 'reject-execution' | 'approve-public' | 'reject-public' | 'suspend'
) {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: async ({
      versionPublicId,
      reason,
    }: {
      versionPublicId: string;
      reason?: string;
    }) => {
      const response = await axiosInstance.post<ApiResponse<MarketplaceVersion>>(
        `/v1/admin/marketplace/versions/${versionPublicId}/${action}`,
        { reason }
      );
      return response.data;
    },
    onSuccess: () => {
      notification.success('Marketplace review decision saved');
      queryClient.invalidateQueries({ queryKey: ['admin-marketplace-review-queue'] });
      queryClient.invalidateQueries({ queryKey: ['admin-marketplace-items'] });
      queryClient.invalidateQueries({ queryKey: ['admin-marketplace-audit-events'] });
    },
    onError: (error: AxiosError<ApiResponse>) => {
      notification.error(error.response?.data?.message || 'Failed to save review decision');
    },
  });
}
